import React, { useMemo, useState, useEffect, useRef } from 'react'
import { NewsArticle } from 'src/models'
import styles from './styles.module.scss'
import Image from 'next/image'
import Link from 'next/link'
import { useAppIntl } from 'src/i18n'
import { newsArticleRoute, newsArticleRouteCategory } from 'src/consts'
import clsx from 'clsx'
import Flag from 'src/components/Flag'

const defaultMainImage = {
  url: '/assets/news-markets.jpg',
}

export interface NewsItemProps {
  className?: string
  newsArticle: NewsArticle
}

export const NewsArticleItem = ({
  className = '',
  newsArticle,
}: NewsItemProps) => {
  const {
    title,
    content,
    teaser: articleTeaser,
    publishedAt,
    readTime,
    slug,
    shortPreview,
  } = newsArticle.attributes

  const intl = useAppIntl()

  const teaser = useMemo(() => {
    if (shortPreview) return shortPreview
    const text = articleTeaser || content
    return text?.length > 250 ? `${text.slice(0, 250)}...` : text
  }, [content, articleTeaser, shortPreview])

  const mainImage =
    newsArticle.attributes.mainImage?.data?.attributes || defaultMainImage

  const categories = (newsArticle.attributes.categories?.data || []).filter(
    category => Boolean(category.attributes.icon?.data)
  )

  const imgContainerRef = useRef(null)
  const [imageHeight, setImageHeight] = useState(0)

  useEffect(() => {
    const calculateHeight = () => {
      const aspectRatio = 16 / 9
      const containerWidth = imgContainerRef.current.offsetWidth
      const calculatedHeight = Math.round(containerWidth / aspectRatio)
      setImageHeight(calculatedHeight)
    }

    calculateHeight()

    const handleResize = () => {
      calculateHeight()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <Link href={newsArticleRoute({ slug })} prefetch={false} legacyBehavior>
      <a className={clsx(styles.articleContainer, className)}>
        <div
          ref={imgContainerRef}
          className={styles.imgContainer}
          style={{ height: imageHeight }}
        >
          <Image
            src={mainImage?.url}
            alt={mainImage?.alternativeText}
            layout='fill'
            className={styles.img}
            objectFit='cover'
            loading='lazy'
          />
          {newsArticle.attributes.flag?.data?.attributes?.description && (
            <Flag
              className={styles.flags}
              text={newsArticle.attributes.flag.data?.attributes?.description}
              backgroundColor={
                newsArticle.attributes.flag.data?.attributes?.backgroundColor
              }
              fontColor={
                newsArticle.attributes.flag.data?.attributes?.fontColor
              }
            />
          )}
        </div>
        <div className={styles.contentContainer}>
          <div className={styles.tags}>
            <div>
              <span className={styles.date}>
                {intl.timeago(new Date(publishedAt))}
              </span>
              <span className={styles.timeRead}>
                {intl.t('newsArticles.readTime', { readTime })}
              </span>
            </div>
            {categories.length > 0 && (
              <div className={styles.categories}>
                {categories.map(category => (
                  <Link
                    key={category.id}
                    href={newsArticleRouteCategory(category.attributes?.slug)}
                    passHref
                  >
                    <Image
                      key={category.key}
                      src={category.attributes.icon?.data?.attributes?.url}
                      alt={category.attributes.name}
                      width={16}
                      height={16}
                    />
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div className={styles.title}>{title}</div>
          <div className={styles.teaser}>
            <div dangerouslySetInnerHTML={{ __html: teaser }} />
          </div>
        </div>
      </a>
    </Link>
  )
}
